import { Badge, Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { CoinCell } from '@/components/ui/coin-cell';
import { AddressDetailDialog } from '@/features/whitelisted-payout-addresses/address-detail-dialog';
import { AddressFormDialog } from '@/features/whitelisted-payout-addresses/address-form-dialog';
import { DeleteAddressSummary } from '@/features/whitelisted-payout-addresses/delete-address-summary';
import { useWhitelistedPayoutAddresses, WHITELISTED_PAYOUT_ADDRESSES_QUERY_KEY } from '@/hooks/queries/compliance/use-whitelisted-payout-addresses';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { deleteWhitelistedPayoutAddress, type WhitelistedPayoutAddress } from '@/services/api/whitelisted-payout-addresses';

const PAGE_SIZE = 50;
const FETCH_ERROR_TOAST_ID = 'whitelisted-addresses-fetch-failed';

const COLUMNS: ColumnDef<WhitelistedPayoutAddress, unknown>[] = [
  {
    accessorKey: 'uuid',
    header: 'UUID',
    cell: ({ row }) => <UuidCell value={row.original.uuid} />,
  },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'type',
    header: 'Type',
    // Legacy chip colors: primary when IBAN, secondary otherwise
    cell: ({ row }) => (
      <Badge className="font-medium" variant={row.original.type === 'IBAN' ? 'default' : 'secondary'}>
        {row.original.type}
      </Badge>
    ),
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    cell: ({ row }) => <CoinCell asset={row.original.currency} />,
  },
  {
    accessorKey: 'iban',
    header: 'IBAN',
    cell: ({ row }) => (row.original.iban ? <span className="font-mono text-xs">{row.original.iban}</span> : '-'),
  },
  {
    accessorKey: 'wallet_address',
    header: 'Wallet Address',
    cell: ({ row }) => (row.original.wallet_address ? <UuidCell value={row.original.wallet_address} /> : '-'),
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    cell: ({ row }) => (row.original.created_at ? <DateCell value={row.original.created_at} /> : '-'),
  },
];

type FormTarget = { mode: 'create' } | { mode: 'edit'; address: WhitelistedPayoutAddress };

const WhitelistedPayoutAddressesPage = () => {
  const queryClient = useQueryClient();
  const [selectedAddress, setSelectedAddress] = useState<WhitelistedPayoutAddress | null>(null);
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);

  const addressesQuery = useWhitelistedPayoutAddresses();
  const { data: addresses, isPending, isError, error, refetch } = addressesQuery;

  useQueryErrorToast(addressesQuery, 'Failed to fetch whitelisted payout addresses', FETCH_ERROR_TOAST_ID);

  const handleEdit = (address: WhitelistedPayoutAddress) => {
    setSelectedAddress(null);
    setFormTarget({ mode: 'edit', address });
  };

  // Shared audit dialog enforces the 35–500 char justification; a throwing
  // onConfirm keeps it open with the upstream error message.
  const handleDelete = async (address: WhitelistedPayoutAddress) => {
    setSelectedAddress(null);
    await confirmAction({
      title: 'Delete Whitelisted Payout Address',
      description: <DeleteAddressSummary address={address} />,
      actionLabel: 'Delete',
      destructive: true,
      onConfirm: async (justification) => {
        await deleteWhitelistedPayoutAddress({ uuid: address.uuid, comment: justification });
        toast.success('Whitelisted payout address deleted successfully');
        await queryClient.invalidateQueries({ queryKey: WHITELISTED_PAYOUT_ADDRESSES_QUERY_KEY });
      },
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-4">
      <PageHeader
        actions={
          <Button className="gap-2" onClick={() => setFormTarget({ mode: 'create' })} type="button">
            <Plus className="size-4" />
            Create New Address
          </Button>
        }
        title="Whitelisted Payout Addresses"
      />

      {isError ? <QueryErrorBanner message={`Failed to fetch whitelisted payout addresses: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}

      <DataTable columns={COLUMNS} data={addresses} emptyMessage="No whitelisted payout addresses" isLoading={isPending} onRowClick={setSelectedAddress} pageSize={PAGE_SIZE} />

      {selectedAddress ? (
        <AddressDetailDialog
          address={selectedAddress}
          onClose={() => setSelectedAddress(null)}
          onDelete={() => handleDelete(selectedAddress)}
          onEdit={() => handleEdit(selectedAddress)}
        />
      ) : null}

      {formTarget ? <AddressFormDialog address={formTarget.mode === 'edit' ? formTarget.address : null} onClose={() => setFormTarget(null)} /> : null}
    </div>
  );
};
WhitelistedPayoutAddressesPage.displayName = 'WhitelistedPayoutAddressesPage';

export default WhitelistedPayoutAddressesPage;
